import { useRef, useState } from "react";

import {
  InputGroup,
  Select,
  InputNumber,
  Input,
  RadioGroup,
  Radio,
  Space
} from "@douyinfe/semi-ui";
import { useTranslation } from "react-i18next";
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  tablePlugin,
  diffSourcePlugin
} from "@mdxeditor/editor";
import {
  CreateLink,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  InsertImage,
  InsertTable,
  GenericDirectiveEditor,
  directivesPlugin,
  DiffSourceToggleWrapper
} from "@mdxeditor/editor";
import {
  type DirectiveDescriptor,
  type MDXEditorMethods,
  type MDXEditorProps
} from "@mdxeditor/editor";
import type { RadioChangeEvent } from "@douyinfe/semi-ui/lib/es/radio";

const Demo = () => {
  const { i18n } = useTranslation();
  const ref = useRef<MDXEditorMethods>(null);
  const editorLanguage = i18n.resolvedLanguage === "zh" ? "zh" : "en";

  const markdown = `hello world`;

  const CalloutDirectiveDescriptor: DirectiveDescriptor = {
    name: "callout",
    testNode(node) {
      return node.name === "callout";
    },
    // set some attribute names to have the editor display a property editor popup.
    attributes: [],
    // used by the generic editor to determine whether or not to render a nested editor.
    hasChildren: true,
    Editor: GenericDirectiveEditor
  };

  return (
    <div
      style={{
        height: "100vh",
        background: "white",
        padding: "24px",
        overflow: "auto"
      }}
    >
      <div style={{ marginTop: "12px" }}>
        <Space>
          <InputGroup className="group-range">
            <div className="label">Size</div>
            <Select style={{ width: "100px" }} defaultValue="signup">
              <Select.Option value="signup">Sign Up</Select.Option>
              <Select.Option value="signin">Sign In</Select.Option>
            </Select>
            <InputNumber
              hideButtons
              placeholder="Email"
              style={{ width: 100 }}
              size="small"
              className="left-input-number"
            />
            <div className="linker">~</div>
            <Input
              placeholder="Email"
              style={{ width: 100 }}
              size="small"
              className="right-input"
            />

            <Select style={{ width: "100px" }} defaultValue="signup">
              <Select.Option value="signup">Sign Up</Select.Option>
              <Select.Option value="signin">Sign In</Select.Option>
            </Select>
            <div className="label">Size</div>
          </InputGroup>

          <RadioGroup
            type="button"
            buttonSize="middle"
            defaultValue={1}
            className="radio-group"
          >
            <Radio value={1}>即时推送</Radio>
            <Radio value={2}>定时推送</Radio>
            <Radio value={3}>动态推送</Radio>
            <Radio value={4}>Last week</Radio>
            <Radio value={5}>Last month</Radio>
          </RadioGroup>
        </Space>
      </div>

      <div style={{ border: "1px solid red" }}>
        <button
          onClick={() =>
            ref.current?.insertMarkdown(
              "This is a **live demo** of MDXEditor with all default features on."
            )
          }
        >
          Insert new markdown
        </button>
        <button onClick={() => console.log(ref.current?.getMarkdown())}>
          Get markdown
        </button>

        <RadioGroup
          type="button"
          buttonSize="middle"
          value={editorLanguage}
          onChange={(value: RadioChangeEvent) => {
            const v = value.target.value as string;
            if (v === "en" || v === "zh") {
              i18n.changeLanguage(v);
            }
          }}
        >
          <Radio value="en">English</Radio>
          <Radio value="zh">中文</Radio>
        </RadioGroup>

        <MDXEditor
          key={editorLanguage}
          ref={ref}
          markdown={markdown}
          translation={(key, defaultValue, interpolations) => {
            return i18n.t(`mdxeditor.${key}`, {
              defaultValue,
              ...(interpolations ?? {})
            });
          }}
          plugins={[
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            thematicBreakPlugin(),
            linkPlugin(),
            linkDialogPlugin(),
            imagePlugin({
              imageUploadHandler: (image: File) => {
                console.log("imageUploadHandler-->", image);
                return Promise.resolve("https://picsum.photos/200/300");
              }
            }),
            tablePlugin(),
            diffSourcePlugin({
              diffMarkdown: "foo"
              // readOnlyDiff: true
            }),
            toolbarPlugin({
              toolbarClassName: "my-classname",
              toolbarContents: () => (
                <DiffSourceToggleWrapper>
                  <UndoRedo />
                  <BoldItalicUnderlineToggles />
                  <BlockTypeSelect />
                  <CreateLink />
                  <InsertImage />
                  <InsertTable />
                </DiffSourceToggleWrapper>
              )
            }),
            directivesPlugin({
              directiveDescriptors: [CalloutDirectiveDescriptor]
            })
          ]}
        />
      </div>
    </div>
  );
};

export default Demo;
